import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import {
  TGetResourceTypesResponse,
  TLocationDoor,
  TOrder,
  TOrder$TikiRental,
  TOrdersList,
} from "../api.js";
import { styled } from "@mui/material/styles";
import { Lock, LockOpen, MoreHoriz } from "@mui/icons-material";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DoorReservation } from "./index.js";
import ResourceTypeDialog from "./ResourceTypeDialog.js";
import DoorActionsDialog from "./DoorActionsDialog.js";
import { RentalListModal } from "./RentalListModal.js";
import {
  filterFirstLevelResourceTypes,
  filterNormalResourceTypes,
  formatContactPersonOrOrderId,
  formatDateTimeFromReservation,
} from "./DoorCardUtils.js";

interface DoorListProps {
  door: TLocationDoor;
  nextReservation: DoorReservation | null;
  nextOrder: TOrder | null;
  tenantKey: string;
  resourceTypes: TGetResourceTypesResponse | null;
  selected: boolean;
  doorResourceTypes: string[];
  locationID: string;
  handleCardClick: (doorId: string | number) => void;
  handleLockClick: (doorId: string | number) => void;
  isAllowedToOpenDoor: boolean;
  rentalsHistory: TOrder$TikiRental[];
  ordersHistory: TOrdersList[] | { error: string };
}

const DoorListBox = styled(Box)(({ theme }) => ({
  minWidth: "225px",
  width: "100%",
  borderRadius: "10px",
  backgroundColor: "#b7b8b8",
  padding: "0px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "@media (max-width: 1037px)": {
    width: "100%",
  },
  "@media (max-width: 792px)": {
    width: "100%",
    minHeight: "0",
  },
  "@media (max-width: 531px)": {
    width: "100%",
    minWidth: "100%",
    minHeight: "0",
  },
}));

const Row = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: "10px",
}));

const RowCell = styled(Box)((theme) => ({
  display: "flex",
  gap: "10px",
  padding: "8px",
  height: "100%",
  alignItems: "center",
}));

const CardCheckbox = styled("input")(({ theme }) => ({
  float: "right",
  minHeight: "25px",
  minWidth: "25px",
  cursor: "pointer",
  margin: "auto 0px auto 0px",
  accentColor: theme.palette.primary.main,
}));

const NoOverflowTypography = styled(Typography)(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  minWidth: "0",
}));

const NoOverFlow = styled("div")(({ theme }) => ({
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  flex: "1",
  minWidth: "0",
}));

const DialogButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

type OpenType = string | null;

export const DoorList = (props: DoorListProps) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState<OpenType>(null);
  const [openDoorActionsDialog, setOpenDoorActionsDialog] = useState(false);

  const formattedContactPersonOrOrderId = formatContactPersonOrOrderId(
    props.nextOrder,
    props.tenantKey,
  );
  const formattedDateTime = formatDateTimeFromReservation(
    props.nextReservation,
  );
  const firstLevelResourceTypes = filterFirstLevelResourceTypes(props);
  const normalResourceTypes = filterNormalResourceTypes(props);

  const handleDoorOptionClick = (option: string) => {
    setOpenDialog(option);
    setOpenDoorActionsDialog(false);
  };

  const handleDoorMenuOpen = () => {
    setOpenDoorActionsDialog(true);
  };

  return (
    <DoorListBox>
      <Row>
        <RowCell
          sx={{ borderRadius: "10px", background: "#D9D9D9", width: "20%" }}
        >
          <CardCheckbox
            type={"checkbox"}
            checked={props.selected}
            onChange={() => {
              props.handleCardClick(props.door.doorID);
            }}
          />
          <Typography variant={"h5"} sx={{ m: "auto 0px auto 0px" }}>
            {(props.door.doorID.length < 2 ? "0" : "") + props.door.doorID}
          </Typography>
          {formattedContactPersonOrOrderId && (
            <Tooltip title={formattedContactPersonOrOrderId}>
              <NoOverflowTypography
                variant={"h5"}
                sx={{ m: "auto 0px auto 0px" }}
              >
                {props.nextReservation && (
                  <Link
                    to={`/tenants/${props.tenantKey}/orders/${props.nextReservation.rental.orderID}`}
                  >
                    {formattedContactPersonOrOrderId}
                  </Link>
                )}
              </NoOverflowTypography>
            </Tooltip>
          )}
        </RowCell>

        <RowCell sx={{ width: "40%" }}>
          {firstLevelResourceTypes && (
            <>
              <Tooltip title={firstLevelResourceTypes.join(", ")}>
                <NoOverflowTypography
                  variant={"h5"}
                  sx={{ fontWeight: "600", m: "auto 0px auto 0px" }}
                >
                  {" "}
                  {firstLevelResourceTypes.join(", ")}{" "}
                </NoOverflowTypography>
              </Tooltip>
              <Box sx={{ display: "inline-block", width: "20%" }}>
                {props.resourceTypes && (
                  <ResourceTypeDialog
                    doorResourceTypes={props.doorResourceTypes}
                    resourceTypes={props.resourceTypes}
                    tenantKey={props.tenantKey}
                    doorID={props.door.doorID}
                    locationID={props.locationID}
                    firstLevelResourceTypes={true}
                    openStatus={openDialog === "edit_tags"}
                    onClose={() => setOpenDialog(null)}
                    showEditIcon={props.resourceTypes.allowCustom}
                  />
                )}
              </Box>
            </>
          )}
        </RowCell>

        <RowCell sx={{ width: "20%" }}>
          {props.nextReservation && (
            <Tooltip title={formattedDateTime}>
              <NoOverflowTypography
                variant={"body1"}
                sx={{ m: "auto 0px auto 0px" }}
              >
                {formattedDateTime}
              </NoOverflowTypography>
            </Tooltip>
          )}
        </RowCell>

        <RowCell sx={{ width: "20%", gap: "0" }}>
          <>
            {props.resourceTypes && normalResourceTypes && (
              <>
                <Tooltip title={normalResourceTypes.join(", ")}>
                  <NoOverFlow>
                    {normalResourceTypes?.map((normalType) => {
                      return (
                        <Chip
                          label={normalType}
                          sx={{ margin: "1px", bgcolor: "#D9D9D9" }}
                          key={normalType}
                        />
                      );
                    })}
                  </NoOverFlow>
                </Tooltip>

                <ResourceTypeDialog
                  doorResourceTypes={props.doorResourceTypes}
                  resourceTypes={props.resourceTypes}
                  tenantKey={props.tenantKey}
                  doorID={props.door.doorID}
                  locationID={props.locationID}
                  firstLevelResourceTypes={false}
                  openStatus={openDialog === "edit_tags"}
                  onClose={() => setOpenDialog(null)}
                  showEditIcon={props.resourceTypes.allowCustom}
                />

                <RentalListModal
                  tenantKey={props.tenantKey}
                  locationID={props.locationID}
                  doorID={props.door.doorID}
                  openStatus={openDialog === "rental_list"}
                  onClose={() => setOpenDialog(null)}
                  rentalsHistory={props.rentalsHistory}
                  ordersHistory={props.ordersHistory}
                />
              </>
            )}

            <Tooltip
              title={
                props.door.openState === "closed"
                  ? t("doorList.unlock")
                  : t("doorList.lock")
              }
              onClick={() =>
                props.isAllowedToOpenDoor
                  ? props.handleLockClick(props.door.doorID)
                  : null
              }
              style={{
                cursor: props.isAllowedToOpenDoor ? "pointer" : "not-allowed",
              }}
            >
              {props.door.openState === "closed" ? (
                <Lock sx={{ fontSize: "25px", ml: "auto" }} />
              ) : (
                <LockOpen sx={{ fontSize: "25px", ml: "auto" }} />
              )}
            </Tooltip>

            <DialogButton
              sx={{ padding: "0px", minWidth: "30px" }}
              onClick={handleDoorMenuOpen}
            >
              <MoreHoriz />
            </DialogButton>
            <DoorActionsDialog
              openStatus={openDoorActionsDialog}
              onClose={() => setOpenDoorActionsDialog(false)}
              onOptionClick={handleDoorOptionClick}
              showRentalList={true}
              showEditTags={true}
              showRentalReassign={props.nextOrder?.tikiOrder.source === "tiki"}
            />
          </>
        </RowCell>
      </Row>
    </DoorListBox>
  );
};
